import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Smartphone, Share, SquarePlus, EllipsisVertical, CheckCircle2, MonitorSmartphone } from "lucide-react";

export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isAndroid = () => /android/i.test(navigator.userAgent);

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start">
    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold grid place-items-center shrink-0">{n}</span>
    <span className="text-sm pt-0.5">{children}</span>
  </li>
);

function Instructions({ prompt, onInstall }) {
  if (isStandalone()) return (
    <p className="text-sm flex items-center gap-2 text-emerald-700" data-testid="install-app-done"><CheckCircle2 className="w-5 h-5" />Ya estás usando la app VIVOTaller.</p>
  );
  if (prompt) return (
    <Button className="w-full rounded-full h-12" onClick={onInstall} data-testid="install-app-btn"><Smartphone className="w-4 h-4 mr-1.5" />Instalar VIVOTaller</Button>
  );
  if (isIOS()) return (
    <ol className="space-y-3" data-testid="install-app-ios-help">
      <Step n={1}>Abre <strong>vivotaller.cl</strong> en <strong>Safari</strong>.</Step>
      <Step n={2}>Toca <Share className="inline w-4 h-4 -mt-0.5 text-primary" /> <strong>Compartir</strong> (abajo en iPhone, arriba en iPad).</Step>
      <Step n={3}>Elige <SquarePlus className="inline w-4 h-4 -mt-0.5 text-primary" /> <strong>«Agregar a pantalla de inicio»</strong> y luego <strong>«Agregar»</strong>.</Step>
    </ol>
  );
  if (isAndroid()) return (
    <ol className="space-y-3" data-testid="install-app-android-help">
      <Step n={1}>Abre <strong>vivotaller.cl</strong> en <strong>Chrome</strong>.</Step>
      <Step n={2}>Toca el menú <EllipsisVertical className="inline w-4 h-4 -mt-0.5 text-primary" /> (arriba a la derecha).</Step>
      <Step n={3}>Elige <strong>«Instalar aplicación»</strong> o <strong>«Agregar a la pantalla principal»</strong>.</Step>
    </ol>
  );
  return (
    <p className="text-sm flex items-start gap-2" data-testid="install-app-desktop-help"><MonitorSmartphone className="w-5 h-5 text-primary shrink-0" /><span>Abre <strong>vivotaller.cl</strong> en tu celular o tablet (Android con Chrome, o iPhone/iPad con Safari) y vuelve a tocar «Instalar App» en tu menú.</span></p>
  );
}

export function InstallAppDialog({ open, onOpenChange, role = "customer" }) {
  const [prompt, setPrompt] = useState(() => window.__vtInstallPrompt || null);
  useEffect(() => {
    const ready = () => setPrompt(window.__vtInstallPrompt);
    const installed = () => { setPrompt(null); onOpenChange(false); };
    window.addEventListener("vt-install-ready", ready);
    window.addEventListener("appinstalled", installed);
    return () => { window.removeEventListener("vt-install-ready", ready); window.removeEventListener("appinstalled", installed); };
  }, [onOpenChange]);
  const install = async () => {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__vtInstallPrompt = null;
    setPrompt(null);
    if (outcome === "accepted") onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="install-app-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="VIVOTaller" className="w-12 h-12 rounded-2xl" />
            <DialogTitle>Instalar App</DialogTitle>
          </div>
          <DialogDescription>{role === "business"
            ? "Pasa lista con QR, marca pagos y revisa tus sesiones con un toque, incluso sin señal."
            : "Ten tus tickets y tu QR de asistencia a un toque, incluso sin internet, y sigue a tus Maestros."}</DialogDescription>
        </DialogHeader>
        <Instructions prompt={prompt} onInstall={install} />
      </DialogContent>
    </Dialog>
  );
}
